import * as React from "react";
import { TrendingUp, Hash, Clock, Target } from 'lucide-react';
import { WizardData } from '../components/PublishingWizard';
import CardComponent from '../ui/CardComponent';
import BadgeComponent from '../ui/BadgeComponent';

interface WizardStep2Props {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
}

const SUGGESTED_HASHTAGS = [
  '#socialmedia',
  '#contentcreator',
  '#digitalmarketing',
  '#socialmediamarketing',
  '#contentmarketing',
  '#marketingstrategy',
  '#brandawareness',
  '#engagement',
];

const SUGGESTED_KEYWORDS = [
  'social media',
  'content strategy',
  'engagement',
  'brand building',
  'digital marketing',
  'audience growth',
];

const BEST_TIMES = [
  { day: 'Monday', time: '10:00 AM - 12:00 PM', platform: 'All Platforms' },
  { day: 'Wednesday', time: '2:00 PM - 4:00 PM', platform: 'LinkedIn' },
  { day: 'Friday', time: '5:00 PM - 7:00 PM', platform: 'Instagram' },
];

export default function WizardStep2({ data, updateData }: WizardStep2Props) {
  const handleHashtagToggle = (hashtag: string) => {
    const hashtags = data.hashtags.includes(hashtag)
      ? data.hashtags.filter((h) => h !== hashtag)
      : [...data.hashtags, hashtag];
    updateData({ hashtags });
  };

  const handleKeywordToggle = (keyword: string) => {
    const keywords = data.keywords.includes(keyword)
      ? data.keywords.filter((k) => k !== keyword)
      : [...data.keywords, keyword];
    updateData({ keywords });
  };

  const calculateSEOScore = () => {
    let score = 0;
    if (data.title.length >= 10) score += 20;
    if (data.description.length >= 50) score += 20;
    if (data.hashtags.length >= 3) score += 20;
    if (data.keywords.length >= 2) score += 20;
    if (data.platforms.length >= 2) score += 20;
    return score;
  };

  const seoScore = calculateSEOScore();

  const getSEOScoreColor = (score: number) => {
    if (score >= 80) return 'text-[#059669]';
    if (score >= 50) return 'text-[#D97706]';
    return 'text-[#DC2626]';
  };

  const getSEOScoreBg = (score: number) => {
    if (score >= 80) return 'bg-[#D1FAE5]';
    if (score >= 50) return 'bg-[#FEF3C7]';
    return 'bg-[#FEE2E2]';
  };

  return (
    <div className="space-y-8">
      {/* SEO Score Panel */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#F9FAFB] border border-[#E5E7EB] rounded flex items-center justify-center">
                <Target size={20} className="text-[#6B7280]" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#111827]">SEO Score</h3>
                <p className="text-xs text-[#6B7280]">Optimize your content for better reach</p>
              </div>
            </div>
            <div
              className={`w-16 h-16 rounded-full ${getSEOScoreBg(seoScore)} flex items-center justify-center`}
            >
              <span className={`text-2xl font-semibold ${getSEOScoreColor(seoScore)}`}>
                {seoScore}
              </span>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Title length</span>
              <BadgeComponent variant={data.title.length >= 10 ? 'success' : 'default'}>
                {data.title.length >= 10 ? '✓ Good' : '✕ Too short'}
              </BadgeComponent>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Description length</span>
              <BadgeComponent variant={data.description.length >= 50 ? 'success' : 'default'}>
                {data.description.length >= 50 ? '✓ Good' : '✕ Too short'}
              </BadgeComponent>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Hashtags</span>
              <BadgeComponent variant={data.hashtags.length >= 3 ? 'success' : 'default'}>
                {data.hashtags.length >= 3 ? '✓ Good' : `Add ${3 - data.hashtags.length} more`}
              </BadgeComponent>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Keywords</span>
              <BadgeComponent variant={data.keywords.length >= 2 ? 'success' : 'default'}>
                {data.keywords.length >= 2 ? '✓ Good' : `Add ${2 - data.keywords.length} more`}
              </BadgeComponent>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#6B7280]">Platforms</span>
              <BadgeComponent variant={data.platforms.length >= 2 ? 'success' : 'default'}>
                {data.platforms.length >= 2 ? '✓ Good' : 'Add more platforms'}
              </BadgeComponent>
            </div>
          </div>
        </div>
      </CardComponent>

      {/* Hashtag Suggestions */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Hash size={18} className="text-[#6B7280]" />
          <label className="text-sm font-medium text-[#111827]">
            Suggested Hashtags
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_HASHTAGS.map((hashtag) => (
            <button
              key={hashtag}
              onClick={() => handleHashtagToggle(hashtag)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                data.hashtags.includes(hashtag)
                  ? 'border-[#2563EB] bg-[#EFF6FF] text-[#2563EB] font-medium'
                  : 'border-[#D1D5DB] bg-white text-[#6B7280] hover:border-[#9CA3AF] hover:bg-[#F9FAFB]'
              }`}
            >
              {hashtag}
            </button>
          ))}
        </div>
        {data.hashtags.length > 0 && (
          <div className="mt-4 p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
            <div className="text-xs font-medium text-[#6B7280] mb-2">
              Selected ({data.hashtags.length}):
            </div>
            <div className="text-sm text-[#111827]">{data.hashtags.join(' ')}</div>
          </div>
        )}
      </div>

      {/* Keyword Suggestions */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={18} className="text-[#6B7280]" />
          <label className="text-sm font-medium text-[#111827]">
            SEO Keywords
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_KEYWORDS.map((keyword) => (
            <button
              key={keyword}
              onClick={() => handleKeywordToggle(keyword)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                data.keywords.includes(keyword)
                  ? 'border-[#2563EB] bg-[#EFF6FF] text-[#2563EB] font-medium'
                  : 'border-[#D1D5DB] bg-white text-[#6B7280] hover:border-[#9CA3AF] hover:bg-[#F9FAFB]'
              }`}
            >
              {keyword}
            </button>
          ))}
        </div>
      </div>

      {/* Best Posting Times */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-[#6B7280]" />
            <h3 className="text-sm font-medium text-[#111827]">
              Best Posting Times
            </h3>
          </div>
          <p className="text-xs text-[#6B7280] mt-1">
            Recommended times based on your audience activity
          </p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {BEST_TIMES.map((time, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg"
              >
                <div>
                  <div className="text-sm font-medium text-[#111827]">{time.day}</div>
                  <div className="text-xs text-[#6B7280] mt-1">{time.platform}</div>
                </div>
                <div className="text-sm font-medium text-[#2563EB]">{time.time}</div>
              </div>
            ))}
          </div>
        </div>
      </CardComponent>
    </div>
  );
}
